import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { extractCollectionSlug } from 'utils/collection';

interface Context {
  routePaths: (string | null)[];
  previousPath: string | null;
  lastVisitedCollection: string | null | undefined;
}

const STORAGE_KEY = 'lastVisitedCollection';

const RouteContext = React.createContext({} as Context);

const RouteProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [routePaths, setRoutePaths] = useState<(string | null)[]>(new Array(4).fill(null));
  const [lastVisitedCollection, setLastVisitedCollection] = useLocalStorage<string | null>(
    STORAGE_KEY,
    null
  );

  const { asPath, events } = useRouter();
  useEffect(() => {
    const routeChangeStart = (): void => {
      setRoutePaths((prev) => {
        const next = [...prev];
        next.shift();
        next.push(asPath);
        return next;
      });

      const collectionSlug = extractCollectionSlug(asPath);
      if (collectionSlug) setLastVisitedCollection(collectionSlug);
    };

    events.on('routeChangeStart', routeChangeStart);
    return () => {
      events.off('routeChangeStart', routeChangeStart);
    };
  }, [events, asPath, setLastVisitedCollection]);

  const previousPath = routePaths[routePaths.length - 1];

  return (
    <RouteContext.Provider
      value={{
        routePaths,
        previousPath,
        lastVisitedCollection,
      }}
    >
      {children}
    </RouteContext.Provider>
  );
};
export default RouteProvider;

const useRouteContext = (): Context => React.useContext(RouteContext);

export { RouteProvider, useRouteContext };
